// @ts-nocheck — mixin methods are composed onto BackendService at runtime; instance API is typed via adminBackendServiceInterface.ts.
import type BaseBackendService from '../BaseBackendService';
import { v4 as uuidv4 } from 'uuid';
import ContentManagementInterface from '../../models/ContentManagementInterface';
import FundOpportunity from '../../models/FundOpportunity';
import { NOT_FOUND } from '../../services/BaseBackendService';
export const adminBackendServiceContentMixin = {
  publishContentManagement(modelType: string, modelId: string): Promise<void> {
    return this.authFetchPromise(async (token) => {
      const response = await this.authenticatedFetch(
        this.apiURL(`contentManagement/publish/${modelType}/${modelId}`),
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
      );
      if (await this.handleAuthRedirect(response)) return;
      if (response.status === 200) return;
      throw 'Unable to publish content';
    });
  },

  revertContentManagement(modelType: string, modelId: string): Promise<void> {
    return this.authFetchPromise(async (token) => {
      const response = await this.authenticatedFetch(
        `/api/contentManagement/revert/${modelType}/${modelId}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
      );
      if (await this.handleAuthRedirect(response)) return;
      if (response.status === 200) return;
      if (response.status === 404) throw 'No published content found to revert to';
      throw 'Unable to revert content';
    });
  },

  uploadHTMLForPDFGeneration(html: string, modelType: string, modelId: string): Promise<string> {
    const htmlBlob = new Blob([html], { type: 'text/html' });
    const htmlFile = new File([htmlBlob], 'html.html', { type: 'text/html' });
    return this.uploadAssetContent('pdfHTML', modelId, modelType, 'html', htmlFile, 'html', false);
  },

  generatePDF(modelType: string, modelId: string, title: string): Promise<string> {
    return this.authFetchPromise(async (token) => {
      const response = await this.authenticatedFetch(`/api/pdf/${modelType}/${modelId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ title }),
      });
      if (await this.handleAuthRedirect(response)) {
        return 'redirecting' as unknown as string;
      }
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate PDF');
      }
      const data = await response.json();
      return data.jobId as string;
    });
  },

  checkPDFJobStatus(jobId: string): Promise<any> {
    return this.authFetchPromise(async (token) => {
      const response = await this.authenticatedFetch(`/api/pdf/jobs/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (await this.handleAuthRedirect(response)) {
        return 'redirecting';
      }
      if (!response.ok) {
        throw new Error('Failed to check job status');
      }
      return response.json();
    });
  },

  uploadPDFHTML(
    html: string,
    modelType: string,
    modelId: string,
    draft: boolean = false,
  ): Promise<string | void> {
    return this.authFetchPromise(async (token) => {
      const response = await this.authenticatedFetch(
        this.apiURL(
          `generateUploadDraftPDFHTMLUrl/${modelType}/${modelId}${draft ? '?draft=true' : ''}`,
        ),
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (await this.handleAuthRedirect(response)) return 'redirecting';
      const data = await response.json();
      const uploadResponse = await this.authenticatedFetch(data.uploadUrl, {
        method: 'PUT',
        credentials: 'omit',
        headers: { 'Content-Type': 'text/html' },
        body: html,
      });
      if (await this.handleAuthRedirect(uploadResponse)) return 'redirecting';
      if (uploadResponse.status === 200) return;
      throw 'Unable to upload HTML';
    });
  },

  previewPDF(html: string, model: string, modelId: string, title: string): Promise<string> {
    return this.uploadPDFHTML(html, model, modelId).then((result) => {
      if (result === 'redirecting') {
        return undefined as unknown as string;
      }
      return this.authFetchPromise(async (token) => {
        const response = await this.authenticatedFetch(
          this.apiURL(`pdf/draft/${model}/${modelId}`),
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ title }),
          },
        );
        if (await this.handleAuthRedirect(response)) {
          return undefined as unknown as string;
        }
        if (response.status !== 200) throw 'Unable to generate PDF';
        const data = await response.json();
        return data.pdfURL as string;
      });
    });
  },

  getContentManagement(
    modelType: string,
    modelId: string,
    token: string,
    draft: boolean = false,
  ): Promise<string | null> {
    return this.getAsset(
      `contentManagement/${modelType}/${modelId}/${draft ? 'draft' : 'published'}.json`,
      token,
      { url: false },
    )
      .then((content) => content as string)
      .catch((error: any) => (error == NOT_FOUND ? null : Promise.reject(error)));
  },

  async getContentManagementWithToken(
    modelType: string,
    modelId: string,
    draft: boolean = false,
  ): Promise<string | null> {
    const token = await this.setupAssetStorageWithRetry(false);

    return await this.getContentManagement(modelType, modelId, token.token.token, draft);
  },

  /**
   * Presigned-upload flow (3 calls) — the old single-call direct-POST
   * version sent the full rich-HTML draft body through Cloud Armor's
   * OWASP CRS XSS/SQLi WAF rules at the load balancer, which occasionally
   * false-positive-blocked legitimate authenticated admin saves. This gets
   * the content bytes off that path entirely: they go straight from the
   * browser to GCS via a signed URL (same pattern uploadPDFHTML already
   * uses below), never through the LB/Cloud Armor-inspected route.
   */
  async uploadContentManagementUpdate(
    modelType: string,
    modelId: string,
    body: ContentManagementInterface,
  ): Promise<{ message: string; version: string }> {
    body.published = false;

    const prepared = await this.authApiMutateAuthed(
      `contentManagement/uploadUrl/${modelType}/${modelId}`,
      'POST',
      undefined,
      (json) =>
        json.data as {
          uploadUrl: string;
          previousUpdateCard: ContentManagementInterface['updateCard'];
        },
    );

    // FundUpdate: the CMS editor only ever manages content[], so without
    // this, saving would silently clobber a separately-saved summary card —
    // mirrors what the old server-side merge used to do before this became
    // a direct-to-GCS upload the backend never sees the body of.
    const bodyToUpload: ContentManagementInterface =
      modelType === 'FundUpdate' && !body.updateCard && prepared.previousUpdateCard
        ? { ...body, updateCard: prepared.previousUpdateCard }
        : body;

    const uploadResponse = await this.authenticatedFetch(prepared.uploadUrl, {
      method: 'PUT',
      credentials: 'omit',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(bodyToUpload),
    });
    if (!uploadResponse.ok) {
      throw new Error('Unable to upload content');
    }

    return this.authApiMutateAuthed(
      `contentManagement/uploadComplete/${modelType}/${modelId}`,
      'POST',
      undefined,
      (json) => json as { message: string; version: string },
    );
  },

  postContentManagement(
    modelType: string,
    modelId: string,
    body: ContentManagementInterface,
  ): Promise<any> {
    return this.uploadContentManagementUpdate(modelType, modelId, body);
  },

  uploadAssetImage(
    modelId: string,
    modelType: string,
    file: File,
    id: string,
    version: string,
    fileExtension: string,
  ): Promise<string> {
    const contentType = file.type || 'application/octet-stream';
    return this.authFetchPromise(async (token) => {
      const response = await this.authenticatedFetch(
        this.apiURL(
          `generateImageUploadUrl?modelId=${modelId}&modelType=${modelType}&fileExtension=${fileExtension}&id=${id}&version=${version}&contentType=${encodeURIComponent(contentType)}`,
        ),
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = await response.json();
      await this.putFileViaXhr(data.uploadUrl, file, contentType);
      return this.normalizeAssetUrl(data.relativeUrl);
    });
  },

  generateImagePreview(imagePath: string): Promise<{
    success: boolean;
    originalPath: string;
    previewPath: string;
    message: string;
  }> {
    return this.authApiMutateAuthed('images/generatePreview', 'POST', { imagePath }, (data) => ({
      success: data.success,
      originalPath: data.originalPath,
      previewPath: data.previewPath,
      message: data.message,
    }));
  },

  uploadAssetContent(
    assetType: 'staticPDFs' | 'images' | 'contentManagement' | 'documents' | 'pdfHTML',
    modelId: string,
    modelType: string,
    fileName: string,
    file: File,
    fileExtension: string,
    randomFileName: boolean = false,
  ): Promise<string> {
    if (randomFileName) {
      fileName = `${uuidv4()}.${fileExtension}`;
    }

    const contentType = file.type || 'application/octet-stream';
    return this.authFetchPromise(async (token) => {
      const response = await this.authenticatedFetch(this.apiURL('generateAssetUploadUrl'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          assetType,
          modelId,
          modelType,
          fileName,
          fileExtension,
          contentType,
        }),
      });
      if (!response.ok) {
        throw new Error(`Failed to generate upload URL: ${response.status}`);
      }
      const data = await response.json();
      await this.putFileViaXhr(data.uploadUrl, file, contentType);
      const returnedUrl = this.normalizeAssetUrl(data.relativeUrl);
      if (assetType === 'images') {
        console.log(`Uploaded image: ${returnedUrl}`);
        const previewData = await this.generateImagePreview(returnedUrl.replace(/^\/api\//, ''));
        console.log(`Preview generated: ${JSON.stringify(previewData)}`);
      }
      return returnedUrl;
    });
  },

  getFundOpportunityContent(
    type: 'FundOpportunity' | 'FundUpdate',
    fundOpportunityId: string,
    draft: boolean = false,
  ): Promise<string | null> {
    return this.setupAssetStorageWithRetry(false).then((token) =>
      this.getContentManagement(type, fundOpportunityId, token.token.token as string, draft),
    );
  },
} satisfies ThisType<BaseBackendService>;
